import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./components/ui/table";
import { Button } from "./components/ui/button";
import {
  Download, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Loader2, ArrowLeft, IndianRupee, FileText, FlaskConical,
  Stethoscope, Calendar, CheckCircle, Clock, XCircle, RotateCcw, Pill,
  ToggleLeft, ToggleRight, Layers, X, Banknote, CreditCard, Smartphone, Wallet, Landmark, Receipt,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `\u20B9${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtC = (v) => {
  if (!v) return "\u20B90";
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 10000000) return `${sign}\u20B9${(a / 10000000).toFixed(2)}Cr`;
  if (a >= 100000) return `${sign}\u20B9${(a / 100000).toFixed(2)}L`;
  if (a >= 1000) return `${sign}\u20B9${(a / 1000).toFixed(1)}K`;
  return fmt(v);
};

const MODE_COLORS = {
  Cash: "#059669", Card: "#2563eb", Credit: "#4f46e5",
  UPI: "#7c3aed", EFT: "#d97706", Wallet: "#0891b2", Others: "#dc2626",
};
const MODE_ICONS = {
  Cash: Banknote, Card: CreditCard, Credit: Receipt, UPI: Smartphone, EFT: Wallet, Wallet: Landmark, Others: FileText,
};
const MODE_KPIS = [
  { key: "Cash", icon: Banknote, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  { key: "Card", icon: CreditCard, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  { key: "Credit", icon: Receipt, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
  { key: "UPI", icon: Smartphone, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
  { key: "EFT", icon: Wallet, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  { key: "Wallet", icon: Landmark, color: "text-cyan-600", bg: "bg-cyan-50", border: "border-cyan-200" },
  { key: "Others", icon: FileText, color: "text-red-500", bg: "bg-red-50", border: "border-red-200" },
];

const SS = {
  PAID: "bg-emerald-100 text-emerald-700", PENDING: "bg-amber-100 text-amber-700",
  UNPAID: "bg-red-100 text-red-700", CANCELLED: "bg-slate-200 text-slate-600",
  REFUNDED: "bg-blue-100 text-blue-700", PENDING_OUTSTANDING: "bg-orange-100 text-orange-700",
};
const TS = {
  OP: "bg-sky-100 text-sky-700", IP: "bg-indigo-100 text-indigo-700",
  ER: "bg-red-100 text-red-700", Registration: "bg-teal-100 text-teal-700",
  Investigation: "bg-violet-100 text-violet-700", MC: "bg-amber-100 text-amber-700",
  Custom: "bg-slate-100 text-slate-700", OT: "bg-pink-100 text-pink-700",
  CR: "bg-cyan-100 text-cyan-700", "WALK-IN": "bg-lime-100 text-lime-700",
};
const Badge = ({ label, styles }) => (
  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${styles || "bg-slate-100 text-slate-600"}`}>{label}</span>
);

export default function BillingReport() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("summary");
  const [mrn, setMrn] = useState("");
  const [patientTypeNumber, setPatientTypeNumber] = useState("");
  const [submittedMrn, setSubmittedMrn] = useState("");
  const [submittedPTN, setSubmittedPTN] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [status, setStatus] = useState("");
  const [billNo, setBillNo] = useState("");
  const [filterOpts, setFilterOpts] = useState({ bill_types: [], payment_statuses: [] });
  const [typeDropOpen, setTypeDropOpen] = useState(false);
  const typeDropRef = useRef(null);
  const [includePharmacy, setIncludePharmacy] = useState(false);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [grand, setGrand] = useState({});
  const [modeSummary, setModeSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [expanded, setExpanded] = useState({});
  const [drillData, setDrillData] = useState({});
  const [drillLoading, setDrillLoading] = useState({});

  const abortRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/billing/report/filters`).then(r => setFilterOpts(r.data)).catch(() => {});
  }, []);

  // Close type dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (typeDropRef.current && !typeDropRef.current.contains(e.target)) setTypeDropOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const typesParam = useMemo(() => selectedTypes.join(","), [selectedTypes]);

  const fetchData = useCallback(async () => {
    if (!submittedMrn && !submittedPTN) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setExpanded({});
    setDrillData({});
    try {
      const params = { include_pharmacy: includePharmacy };
      if (submittedMrn) params.mrn = submittedMrn;
      if (submittedPTN) params.patient_type_number = submittedPTN;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (typesParam) params.bill_types = typesParam;
      if (status) params.status = status;
      if (billNo.trim()) params.bill_no = billNo.trim();

      if (mode === "summary") {
        const { data: res } = await axios.get(`${API}/api/billing/report/summary`, { params, signal: controller.signal });
        setData(res.data);
        setTotal(res.grand?.totalBills || 0);
        setTotalPages(0);
        setGrand(res.grand || {});
        setModeSummary(res.modeSummary || {});
      } else {
        params.page = page;
        params.page_size = 50;
        const { data: res } = await axios.get(`${API}/api/billing/report`, { params, signal: controller.signal });
        setData(res.data);
        setTotal(res.total);
        setTotalPages(res.total_pages);
        setGrand(res.grand || {});
        setModeSummary(res.modeSummary || {});
      }
      setSearched(true);
    } catch (err) {
      if (!axios.isCancel(err)) toast.error("Failed to load billing report");
    }
    setLoading(false);
  }, [submittedMrn, submittedPTN, page, dateFrom, dateTo, typesParam, status, mode, includePharmacy, billNo]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [submittedMrn, submittedPTN, dateFrom, dateTo, typesParam, status, mode]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!mrn.trim() && !patientTypeNumber.trim()) { toast.error("Please enter an MRN or Admission/IP Number"); return; }
    setSubmittedMrn(mrn.trim());
    setSubmittedPTN(patientTypeNumber.trim());
  };

  const toggleType = (val) => {
    setSelectedTypes(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);
  };

  const toggleSummaryDrill = useCallback(async (billType) => {
    if (expanded[billType]) {
      setExpanded(prev => { const n = { ...prev }; delete n[billType]; return n; });
      return;
    }
    setExpanded(prev => ({ ...prev, [billType]: true }));
    if (drillData[billType]) return;
    setDrillLoading(prev => ({ ...prev, [billType]: true }));
    try {
      const params = { bill_type: billType };
      if (submittedMrn) params.mrn = submittedMrn;
      if (submittedPTN) params.patient_type_number = submittedPTN;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (status) params.status = status;
      const { data: res } = await axios.get(`${API}/api/billing/report/summary/drill-down`, { params });
      setDrillData(prev => ({ ...prev, [billType]: res.rows || [] }));
    } catch { toast.error("Failed to load details"); }
    setDrillLoading(prev => ({ ...prev, [billType]: false }));
  }, [expanded, drillData, submittedMrn, submittedPTN, dateFrom, dateTo, status]);

  const toggleDetailDrill = useCallback(async (billId) => {
    if (expanded[billId]) {
      setExpanded(prev => { const n = { ...prev }; delete n[billId]; return n; });
      return;
    }
    setExpanded(prev => ({ ...prev, [billId]: true }));
    if (drillData[billId]) return;
    setDrillLoading(prev => ({ ...prev, [billId]: true }));
    try {
      const { data: detail } = await axios.get(`${API}/api/billing/report/detail/${billId}`, { params: { include_pharmacy: includePharmacy } });
      setDrillData(prev => ({ ...prev, [billId]: detail }));
    } catch { toast.error("Failed to load bill details"); }
    setDrillLoading(prev => ({ ...prev, [billId]: false }));
  }, [expanded, drillData, includePharmacy]);

  const handleExportPdf = async () => {
    if (!submittedMrn && !submittedPTN) return;
    try {
      toast.info("Generating PDF...");
      const params = { include_pharmacy: includePharmacy };
      if (submittedMrn) params.mrn = submittedMrn;
      if (submittedPTN) params.patient_type_number = submittedPTN;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (typesParam) params.bill_types = typesParam;
      if (status) params.status = status;
      const { data: pdfData } = await axios.get(`${API}/api/billing/report/export-pdf`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([pdfData], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `billing_report_${submittedMrn || submittedPTN}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch { toast.error("Failed to generate PDF"); }
  };

  const handleExport = async () => {
    if (!submittedMrn && !submittedPTN) return;
    try {
      toast.info("Generating CSV...");
      const params = { mode, include_pharmacy: includePharmacy };
      if (submittedMrn) params.mrn = submittedMrn;
      if (submittedPTN) params.patient_type_number = submittedPTN;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (typesParam) params.bill_types = typesParam;
      if (status) params.status = status;
      if (billNo.trim()) params.bill_no = billNo.trim();
      const r = await axios.get(`${API}/api/billing/report/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `billing_${submittedMrn}_${mode}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  // Group detailed data by date
  const groupedByDate = useMemo(() => {
    if (mode !== "detailed") return [];
    const groups = {};
    for (const bill of data) {
      const key = bill.dateKey || "unknown";
      if (!groups[key]) groups[key] = { dateKey: key, dateLabel: bill.dateLabel || key, bills: [], totalAmount: 0 };
      groups[key].bills.push(bill);
      groups[key].totalAmount += (bill.grandTotal || 0);
    }
    return Object.values(groups);
  }, [data, mode]);

  const kpiCards = useMemo(() => {
    const g = grand;
    return [
      { icon: IndianRupee, label: "Total Billed", value: fmtC(g.totalAmount || 0), bg: "bg-slate-900", textColor: "text-white", iconColor: "text-slate-400" },
      { icon: CheckCircle, label: `Paid (${g.paidCount || 0})`, value: fmtC(g.paidAmount || 0), color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
      { icon: Clock, label: `Pending (${g.pendingCount || 0})`, value: fmtC(g.pendingAmount || 0), color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
      { icon: XCircle, label: `Cancelled (${g.cancelledCount || 0})`, value: fmtC(g.cancelledAmount || 0), color: "text-slate-600", bg: "bg-slate-100", border: "border-slate-200" },
      { icon: RotateCcw, label: `Refunded (${g.refundedCount || 0})`, value: fmtC(g.refundedAmount || 0), color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
      { icon: FileText, label: "Total Bills", value: `${g.totalBills || 0}`, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
    ];
  }, [grand]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1440px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="billing-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900" data-testid="billing-title">Billing Report</h1>
              <p className="text-xs text-slate-500">Patient-wise billing lookup by MRN or Admission Number</p>
            </div>
          </div>
          {(submittedMrn || submittedPTN) && grand.patientName && (
            <div className="text-right">
              <p className="text-sm font-bold text-slate-800">{grand.patientName}</p>
              <p className="text-xs text-slate-500">
                {submittedMrn ? `MRN: ${submittedMrn}` : ""}
                {submittedMrn && submittedPTN ? " | " : ""}
                {submittedPTN ? `Adm#: ${submittedPTN}` : ""}
              </p>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-6 space-y-4">
        {/* Search + Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="billing-filters">
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
            <Fld label="MRN Number">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <input value={mrn} onChange={e => setMrn(e.target.value)} placeholder="Enter MRN..."
                  className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 font-semibold"
                  data-testid="billing-mrn-input" />
              </div>
            </Fld>
            <Fld label="Admission / IP No">
              <div className="relative">
                <FileText className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input value={patientTypeNumber} onChange={e => setPatientTypeNumber(e.target.value)} placeholder="e.g. IP12345"
                  className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 font-semibold"
                  data-testid="billing-ptn-input" />
              </div>
            </Fld>
            <Fld label="Bill No">
              <div className="relative">
                <FileText className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input value={billNo} onChange={e => setBillNo(e.target.value)} placeholder="e.g. CB2601850"
                  className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                  data-testid="billing-billno-input" />
              </div>
            </Fld>
            <Fld label="From">
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                  data-testid="billing-date-from" />
              </div>
            </Fld>
            <Fld label="To">
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                  data-testid="billing-date-to" />
              </div>
            </Fld>
            {/* Multi-select Bill Type */}
            <Fld label="Bill Type">
              <div className="relative" ref={typeDropRef}>
                <button type="button" onClick={() => setTypeDropOpen(p => !p)} data-testid="billing-type-toggle"
                  className="w-56 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 hover:bg-white text-left flex items-center justify-between">
                  {selectedTypes.length === 0 ? (
                    <span className="text-slate-400">All Types</span>
                  ) : (
                    <span className="text-slate-700 font-medium truncate">{selectedTypes.length} type{selectedTypes.length > 1 ? "s" : ""} selected</span>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                </button>
                {selectedTypes.length > 0 && (
                  <button type="button" onClick={() => setSelectedTypes([])}
                    className="absolute right-8 top-2.5 text-slate-400 hover:text-slate-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                {typeDropOpen && (
                  <div className="absolute z-30 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto py-1">
                    {filterOpts.bill_types.map(t => (
                      <label key={t.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={selectedTypes.includes(t.value)} onChange={() => toggleType(t.value)}
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                        <Badge label={t.label} styles={TS[t.value]} />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </Fld>
            <Fld label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-44 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10"
                data-testid="billing-status-filter">
                <option value="">All Statuses</option>
                {filterOpts.payment_statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Fld>
            <Fld label="Pharmacy">
              <button type="button" onClick={() => setIncludePharmacy(p => !p)} data-testid="billing-pharmacy-toggle"
                className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-all ${includePharmacy ? "bg-orange-50 border-orange-300 text-orange-700" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
                {includePharmacy ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                <Pill className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">{includePharmacy ? "Included" : "Excluded"}</span>
              </button>
            </Fld>
            <Button type="submit" size="sm" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="billing-search-btn">
              <Search className="w-3.5 h-3.5 mr-1" /> Search
            </Button>
            {(submittedMrn || submittedPTN) && (
              <div className="flex gap-2 ml-auto">
                <Button type="button" size="sm" variant="outline" onClick={handleExportPdf} data-testid="billing-export-pdf"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={handleExport} data-testid="billing-export"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
              </div>
            )}
          </form>
          {/* Selected types pills */}
          {selectedTypes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2.5 border-t border-slate-100">
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider self-center mr-1">Types:</span>
              {selectedTypes.map(t => {
                const opt = filterOpts.bill_types.find(o => o.value === t);
                return (
                  <button key={t} type="button" onClick={() => toggleType(t)}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${TS[t] || "bg-slate-100 text-slate-600"}`}>
                    {opt?.label || t} <X className="w-2.5 h-2.5" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!searched && !loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center" data-testid="billing-empty">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-600">Enter an MRN or Admission Number to view billing report</h2>
            <p className="text-sm text-slate-400 mt-1">Search by Medical Record Number or IP/Admission Number to see summary or detailed bills</p>
          </div>
        )}

        {searched && (
          <>
            {/* Mode Toggle + KPI */}
            <div className="flex items-start gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 shrink-0 w-48">
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 block">Report Mode</label>
                <div className="space-y-2">
                  {[["summary", "Summary"], ["detailed", "Detailed"]].map(([key, label]) => (
                    <button key={key} onClick={() => setMode(key)} data-testid={`mode-${key}`}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === key ? "bg-slate-900 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {mode === key ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="billing-kpis">
                {kpiCards.map((kpi, i) => (
                  <div key={i} className={`${kpi.bg} ${kpi.border ? `border ${kpi.border}` : ""} rounded-xl p-3.5`} data-testid={`billing-kpi-${i}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <kpi.icon className={`w-3.5 h-3.5 ${kpi.iconColor || kpi.color}`} />
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${kpi.bg === "bg-slate-900" ? "text-slate-400" : "text-slate-500"}`}>{kpi.label}</span>
                    </div>
                    <p className={`text-lg font-bold ${kpi.textColor || kpi.color}`}>{kpi.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown — grouped by Hospital Charges & Pharmacy/Medicine */}
            {total > 0 && (
              <div className={`grid ${includePharmacy ? "grid-cols-3" : "grid-cols-2"} gap-3`} data-testid="billing-entry-counts">
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Stethoscope className="w-4 h-4 text-teal-700" />
                    <span className="text-xs font-bold text-teal-800 uppercase tracking-wider">Hospital Charges</span>
                  </div>
                  <p className="text-xl font-bold text-teal-800">{fmt(grand.hospitalChargesAmount || 0)}</p>
                  <p className="text-[10px] text-teal-600 mt-1">{grand.hospitalCharges || 0} entries</p>
                </div>
                {includePharmacy && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Pill className="w-4 h-4 text-orange-700" />
                      <span className="text-xs font-bold text-orange-800 uppercase tracking-wider">Pharmacy / Medicine</span>
                    </div>
                    <p className="text-xl font-bold text-orange-800">{fmt(grand.medicineItemsAmount || 0)}</p>
                    <p className="text-[10px] text-orange-600 mt-1">{grand.medicineItems || 0} items</p>
                  </div>
                )}
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FlaskConical className="w-4 h-4 text-violet-700" />
                    <span className="text-xs font-bold text-violet-800 uppercase tracking-wider">Lab Tests</span>
                  </div>
                  <p className="text-xl font-bold text-violet-800">{fmt(grand.labTestsAmount || 0)}</p>
                  <p className="text-[10px] text-violet-600 mt-1">{grand.labTests || 0} tests</p>
                </div>
              </div>
            )}

            {/* Payment Mode Summary — transaction-based */}
            {modeSummary && modeSummary.total !== undefined && modeSummary.total !== 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="billing-mode-summary">
                <div className="flex items-center gap-2 mb-3">
                  <IndianRupee className="w-4 h-4 text-slate-600" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Payment Mode Breakdown (Transaction-Based)</span>
                  <span className="ml-auto text-sm font-bold text-slate-900">Total: {fmt(modeSummary.total)}</span>
                </div>
                <div className="grid grid-cols-8 gap-2">
                  {MODE_KPIS.map(m => {
                    const val = modeSummary[m.key] || 0;
                    const pct = modeSummary.total ? ((Math.abs(val) / Math.abs(modeSummary.total)) * 100).toFixed(1) : "0.0";
                    const Icon = m.icon;
                    return (
                      <div key={m.key} className={`${m.bg} border ${m.border} rounded-lg p-3 text-center`} data-testid={`mode-card-${m.key}`}>
                        <Icon className={`w-4 h-4 mx-auto mb-1 ${m.color}`} />
                        <p className={`text-xs font-bold ${m.color}`}>{m.key}</p>
                        <p className={`text-sm font-bold ${m.color} mt-0.5`}>{fmt(val)}</p>
                        <p className="text-[9px] text-slate-400">{pct}%</p>
                      </div>
                    );
                  })}
                  <div className="bg-slate-900 rounded-lg p-3 text-center">
                    <IndianRupee className="w-4 h-4 mx-auto mb-1 text-slate-400" />
                    <p className="text-xs font-bold text-slate-400">TOTAL</p>
                    <p className="text-sm font-bold text-white mt-0.5">{fmt(modeSummary.total)}</p>
                    <p className="text-[9px] text-slate-500">100%</p>
                  </div>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="billing-table">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-500 font-medium">
                  {mode === "summary" ? `${data.length} bill types` : `${total} bills | Page ${page} of ${totalPages || 1}`}
                  <span className="text-slate-400 ml-2">(click row to expand details)</span>
                </span>
                {mode === "detailed" && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="billing-prev"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="billing-next"><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                {mode === "summary" ? (
                  <SummaryTable data={data} loading={loading} grand={grand} expanded={expanded} drillData={drillData} drillLoading={drillLoading} toggleDrill={toggleSummaryDrill} includePharmacy={includePharmacy} />
                ) : (
                  <DetailedTable groups={groupedByDate} loading={loading} grand={grand} data={data} expanded={expanded} drillData={drillData} drillLoading={drillLoading} toggleDrill={toggleDetailDrill} includePharmacy={includePharmacy} />
                )}
              </div>

              {mode === "detailed" && totalPages > 1 && (
                <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-xs font-semibold text-slate-600 px-2 py-1">{page} / {totalPages}</span>
                    <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SUMMARY TABLE — grouped by Bill Type
   ═══════════════════════════════════════════ */
function SummaryTable({ data, loading, grand, expanded, drillData, drillLoading, toggleDrill, includePharmacy }) {
  const headers = ["Bill Type", "Bills", "Total Amount", "Paid", "Pending", "Cancelled", "Refunded", "Tax", "Discount", "Charges"];
  if (includePharmacy) headers.push("Medicines");
  headers.push("Lab Tests", "Date Range");
  const colSpan = headers.length + 1;
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead className="w-8"></TableHead>
          {headers.map(h => (
            <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && <TableRow><TableCell colSpan={colSpan} className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading...</TableCell></TableRow>}
        {!loading && data.length === 0 && <TableRow><TableCell colSpan={colSpan} className="text-center py-10 text-slate-400">No bills found</TableCell></TableRow>}
        {!loading && data.map((row, i) => {
          const key = row.billType;
          const isOpen = !!expanded[key];
          return (
            <React.Fragment key={key}>
              <TableRow className={`cursor-pointer transition-colors ${isOpen ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                onClick={() => toggleDrill(key)} data-testid={`summary-row-${i}`}>
                <TableCell className="w-8 px-2">{isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
                <TableCell className="text-xs"><Badge label={row.billTypeLabel} styles={TS[row.billType]} /></TableCell>
                <TableCell className="text-xs text-center tabular-nums font-semibold">{row.count}</TableCell>
                <TableCell className="text-xs text-right tabular-nums font-bold text-slate-800">{fmt(row.totalAmount)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-emerald-700">{fmt(row.paidAmount)} <span className="text-[9px] text-emerald-500">({row.paidCount})</span></TableCell>
                <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(row.pendingAmount)} <span className="text-[9px] text-amber-500">({row.pendingCount})</span></TableCell>
                <TableCell className="text-xs text-right tabular-nums text-slate-500">{fmt(row.cancelledAmount)} <span className="text-[9px]">({row.cancelledCount})</span></TableCell>
                <TableCell className="text-xs text-right tabular-nums text-blue-600">{fmt(row.refundedAmount)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(row.totalTax)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(row.totalDiscount)}</TableCell>
                <TableCell className="text-xs text-center tabular-nums">{row.hospitalCharges}</TableCell>
                {includePharmacy && <TableCell className="text-xs text-center tabular-nums">{row.medicineItems}</TableCell>}
                <TableCell className="text-xs text-center tabular-nums">{row.labTests}</TableCell>
                <TableCell className="text-[10px] text-slate-400 whitespace-nowrap">
                  {row.firstDate ? new Date(row.firstDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" }) : ""} - {row.lastDate ? new Date(row.lastDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : ""}
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow className="bg-slate-50/80">
                  <TableCell colSpan={colSpan} className="p-0">
                    <SummaryDrillPanel rows={drillData[key]} isLoading={drillLoading[key]} />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
        {!loading && data.length > 0 && (
          <TableRow className="bg-slate-900" data-testid="summary-total-row">
            <TableCell></TableCell>
            <TableCell className="text-xs font-bold text-white">GRAND TOTAL ({grand.typeCount || data.length} types)</TableCell>
            <TableCell className="text-xs text-center font-bold text-white">{grand.totalBills}</TableCell>
            <TableCell className="text-xs text-right font-bold text-white">{fmt(grand.totalAmount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-emerald-300">{fmt(grand.paidAmount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-amber-300">{fmt(grand.pendingAmount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-slate-400">{fmt(grand.cancelledAmount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-blue-300">{fmt(grand.refundedAmount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-amber-300">{fmt(grand.totalTax)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-white">{fmt(grand.totalDiscount)}</TableCell>
            <TableCell className="text-xs text-center font-bold text-white">{grand.hospitalCharges}</TableCell>
            {includePharmacy && <TableCell className="text-xs text-center font-bold text-white">{grand.medicineItems}</TableCell>}
            <TableCell className="text-xs text-center font-bold text-white">{grand.labTests}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function SummaryDrillPanel({ rows, isLoading }) {
  if (isLoading) return <div className="flex items-center gap-2 px-6 py-4 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading bills...</div>;
  if (!rows || rows.length === 0) return <div className="px-6 py-3 text-xs text-slate-400">No bills found</div>;
  return (
    <div className="border-l-4 border-indigo-300 ml-4 my-2">
      <Table>
        <TableHeader>
          <TableRow className="bg-indigo-50/50">
            {["Bill No", "Date", "Adm No", "Scheme", "Amount", "Tax", "Discount", "Grand Total", "Pay Mode", "Paid (Txn)", "Status", "Billed By", "Claim Status"].map(h => (
              <TableHead key={h} className="text-[9px] font-bold uppercase tracking-wider text-indigo-500 whitespace-nowrap py-1.5 px-2">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((b, i) => (
            <TableRow key={i} className="hover:bg-white/60">
              <TableCell className="text-[11px] font-mono font-semibold text-slate-700 px-2 py-1.5">
                <div>{b.billNo || "-"}</div>
                {b.isMergedRoot && <span className="text-[8px] font-bold text-indigo-500 bg-indigo-50 px-1 py-0.5 rounded mt-0.5 inline-block">Consolidated</span>}
                {b.mergedOrderIds?.length > 0 && (
                  <div className="mt-0.5 space-y-0">
                    {b.mergedOrderIds.map((oid, idx) => (
                      <div key={idx} className="text-[8px] font-mono text-slate-400 truncate max-w-[120px]" title={oid}>{oid}</div>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-[11px] tabular-nums px-2 py-1.5 whitespace-nowrap">{b.createdAtIST || "-"}</TableCell>
              <TableCell className="text-[11px] text-slate-600 px-2 py-1.5">{b.patientTypeNumber || "-"}</TableCell>
              <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{b.schemeName || "-"}</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(b.totalAmount)}</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums text-amber-700 px-2 py-1.5">{fmt(b.totalTax)}</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(b.totalDiscount)}</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums font-semibold px-2 py-1.5">{fmt(b.grandTotal)}</TableCell>
              <TableCell className="px-2 py-1.5">
                {b.payModes && Object.keys(b.payModes).length > 0 ? (
                  <div className="flex flex-wrap gap-0.5">
                    {Object.entries(b.payModes).map(([mode, amt]) => (
                      <span key={mode} className="px-1.5 py-0.5 rounded text-[8px] font-bold whitespace-nowrap"
                        style={{ backgroundColor: (MODE_COLORS[mode] || "#94a3b8") + "18", color: MODE_COLORS[mode] || "#64748b" }}>
                        {mode} {fmt(amt)}
                      </span>
                    ))}
                  </div>
                ) : <span className="text-[10px] text-slate-300">-</span>}
              </TableCell>
              <TableCell className="text-[11px] text-right tabular-nums font-semibold text-emerald-700 px-2 py-1.5">{b.paidAmountTxn ? fmt(b.paidAmountTxn) : "-"}</TableCell>
              <TableCell className="px-2 py-1.5"><Badge label={b.paymentStatus} styles={SS[b.paymentStatus]} /></TableCell>
              <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{b.billedBy || "-"}</TableCell>
              <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{b.claimStatus || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ═══════════════════════════════════════════
   DETAILED TABLE — grouped by date
   ═══════════════════════════════════════════ */
function DetailedTable({ groups, loading, grand, data, expanded, drillData, drillLoading, toggleDrill, includePharmacy }) {
  const headers = ["Bill No", "Type", "Time", "Charges"];
  if (includePharmacy) headers.push("Medicines");
  headers.push("Labs", "Amount", "Tax", "Discount", "Grand Total", "Pay Mode", "Paid (Txn)", "Status", "Billed By");
  const colSpan = headers.length + 1;
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead className="w-8"></TableHead>
          {headers.map(h => (
            <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && <TableRow><TableCell colSpan={colSpan} className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading...</TableCell></TableRow>}
        {!loading && groups.length === 0 && <TableRow><TableCell colSpan={colSpan} className="text-center py-10 text-slate-400">No bills found</TableCell></TableRow>}
        {!loading && groups.map((group, gi) => (
          <React.Fragment key={group.dateKey}>
            {/* Date Group Header */}
            <TableRow className="bg-indigo-50 border-t-2 border-indigo-200" data-testid={`date-group-${gi}`}>
              <TableCell></TableCell>
              <TableCell colSpan={colSpan - 6} className="text-xs font-bold text-indigo-800">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                  {group.dateLabel} ({group.bills.length} bill{group.bills.length > 1 ? "s" : ""})
                </div>
              </TableCell>
              <TableCell className="text-xs text-right font-bold text-indigo-800">{fmt(group.totalAmount)}</TableCell>
              <TableCell colSpan={4}></TableCell>
            </TableRow>
            {/* Bills in this date */}
            {group.bills.map((bill, bi) => {
              const bid = bill.billId;
              const isOpen = !!expanded[bid];
              const timeStr = bill.createdAtIST ? bill.createdAtIST.split(" ").slice(1).join(" ") : "-";
              return (
                <React.Fragment key={bid || bi}>
                  <TableRow className={`cursor-pointer transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-slate-50"}`}
                    onClick={() => toggleDrill(bid)} data-testid={`detail-row-${gi}-${bi}`}>
                    <TableCell className="w-8 px-2">{isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
                    <TableCell className="text-xs font-mono font-semibold text-slate-800">{bill.billNo || "-"}</TableCell>
                    <TableCell className="text-xs"><Badge label={bill.billTypeLabel || bill.billType} styles={TS[bill.billType]} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap tabular-nums text-slate-600">{timeStr}</TableCell>
                    <TableCell className="text-xs text-center tabular-nums">{bill.chargesCount || 0}</TableCell>
                    {includePharmacy && <TableCell className="text-xs text-center tabular-nums">{bill.medicinesCount || 0}</TableCell>}
                    <TableCell className="text-xs text-center tabular-nums">{bill.labTestsCount || 0}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{fmt(bill.totalAmount)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(bill.totalTax)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{fmt(bill.totalDiscount)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-bold text-slate-800">{fmt(bill.grandTotal)}</TableCell>
                    <TableCell>
                      {bill.payModes && Object.keys(bill.payModes).length > 0 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {Object.entries(bill.payModes).map(([mode, amt]) => (
                            <span key={mode} className="px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap"
                              style={{ backgroundColor: (MODE_COLORS[mode] || "#94a3b8") + "18", color: MODE_COLORS[mode] || "#64748b" }}>
                              {mode} {fmt(amt)}
                            </span>
                          ))}
                        </div>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold text-emerald-700">{bill.paidAmountTxn ? fmt(bill.paidAmountTxn) : "-"}</TableCell>
                    <TableCell className="text-xs"><Badge label={bill.paymentStatus} styles={SS[bill.paymentStatus]} /></TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">{bill.billedBy || "-"}</TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-slate-50/80">
                      <TableCell colSpan={colSpan} className="p-0">
                        <DrillPanel detail={drillData[bid]} isLoading={drillLoading[bid]} includePharmacy={includePharmacy} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
        {!loading && data.length > 0 && (
          <TableRow className="bg-slate-900" data-testid="detail-total-row">
            <TableCell></TableCell>
            <TableCell className="text-xs font-bold text-white" colSpan={includePharmacy ? 6 : 5}>GRAND TOTAL ({grand.totalBills} bills)</TableCell>
            <TableCell className="text-xs text-right font-bold text-white">{fmt(grand.totalAmount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-amber-300">{fmt(grand.totalTax)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-white">{fmt(grand.totalDiscount)}</TableCell>
            <TableCell className="text-xs text-right font-bold text-white">{fmt(grand.totalAmount)}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell colSpan={2}></TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

/* ─── Line Item Drill-down Panel ─── */
function DrillPanel({ detail, isLoading, includePharmacy }) {
  if (isLoading) return <div className="flex items-center gap-2 px-6 py-4 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...</div>;
  if (!detail) return null;

  const charges = detail.hospitalChargeEntries || [];
  const medicines = includePharmacy ? (detail.medicineEntries || []) : [];
  const labs = detail.labTestEntries || [];
  const orders = detail.orders || [];
  const txns = detail.transactions || [];
  const isCB = detail.isMergedRoot;

  return (
    <div className="border-l-4 border-blue-300 ml-4 my-2 space-y-3 px-4 py-3">
      {/* ── Bill & Patient Info ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-[11px]">
        <InfoRow label="Bill No" value={detail.billNo} bold />
        <InfoRow label="Bill Type" value={detail.billTypeLabel || detail.billType} />
        <InfoRow label="Patient" value={detail.patientName} />
        <InfoRow label="MRN" value={detail.MRN} />
        <InfoRow label="Admission No" value={detail.patientTypeNumber} />
        <InfoRow label="Patient Type" value={detail.patientType} />
        <InfoRow label="Scheme" value={detail.schemeName} />
        <InfoRow label="Claim Status" value={detail.claimStatus} />
        <InfoRow label="Billed At" value={detail.createdAtIST} />
        <InfoRow label="Payment Status" value={detail.paymentStatus} highlight />
      </div>

      {/* ── Orders ── */}
      {orders.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Layers className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
              {isCB ? `Consolidated Orders (${orders.length})` : "Order Details"}
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50/50">
                <TableHead className="text-[9px] font-bold uppercase text-slate-500 py-1.5 px-2">Order ID</TableHead>
                <TableHead className="text-[9px] font-bold uppercase text-slate-500 py-1.5 px-2">Type</TableHead>
                <TableHead className="text-[9px] font-bold uppercase text-slate-500 py-1.5 px-2">Created By</TableHead>
                <TableHead className="text-[9px] font-bold uppercase text-slate-500 py-1.5 px-2">IP / Bed / Room</TableHead>
                <TableHead className="text-[9px] font-bold uppercase text-slate-500 py-1.5 px-2">Time</TableHead>
                <TableHead className="text-[9px] font-bold uppercase text-slate-500 py-1.5 px-2 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o, i) => (
                <TableRow key={i} className="hover:bg-white/60">
                  <TableCell className="text-[11px] font-mono font-semibold text-slate-800 px-2 py-1.5">{o.orderId}</TableCell>
                  <TableCell className="text-[11px] px-2 py-1.5 capitalize">{o.orderType}</TableCell>
                  <TableCell className="text-[11px] px-2 py-1.5 text-slate-600">{o.createdByName || "-"}</TableCell>
                  <TableCell className="text-[11px] px-2 py-1.5 text-slate-500">
                    {[o.ipNumber, o.bedNumber, o.roomNumber].filter(Boolean).join(" / ") || "-"}
                  </TableCell>
                  <TableCell className="text-[11px] px-2 py-1.5 tabular-nums text-slate-500">{o.createdAt || "-"}</TableCell>
                  <TableCell className="text-[11px] px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(o.grandTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* ── Payment Transactions ── */}
      {txns.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <IndianRupee className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Payment Transactions ({txns.length})</span>
            <span className="ml-auto text-[11px] font-bold text-emerald-800">Paid: {fmt(detail.paidAmountTxn)}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {txns.map((t, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                style={{ backgroundColor: (MODE_COLORS[t.category] || "#94a3b8") + "10", borderColor: (MODE_COLORS[t.category] || "#94a3b8") + "40" }}>
                {(() => { const Icon = MODE_ICONS[t.category] || FileText; return <Icon className="w-3.5 h-3.5" style={{ color: MODE_COLORS[t.category] || "#64748b" }} />; })()}
                <div>
                  <p className="text-[11px] font-bold" style={{ color: MODE_COLORS[t.category] || "#64748b" }}>{t.category}</p>
                  <p className="text-sm font-bold text-slate-800">{fmt(t.amount)}</p>
                </div>
                {t.referenceNumber && <span className="text-[9px] font-mono text-slate-400 ml-1">Ref: {t.referenceNumber}</span>}
                {t.status === "PAYOUT" && <span className="text-[9px] font-bold text-red-600 ml-1">REFUND</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Line Items ── */}
      {charges.length > 0 && <EntryTable title="Hospital Charges" icon={<Stethoscope className="w-3.5 h-3.5 text-teal-600" />} color="teal" entries={charges} showDept />}
      {medicines.length > 0 && <EntryTable title="Medicines" icon={<Pill className="w-3.5 h-3.5 text-orange-600" />} color="orange" entries={medicines} showBatch />}
      {labs.length > 0 && <EntryTable title="Lab Tests" icon={<FlaskConical className="w-3.5 h-3.5 text-violet-600" />} color="violet" entries={labs} showDept />}

      {/* ── Financial Summary ── */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] text-slate-500 pt-2 border-t border-slate-200">
        <span>Subtotal: <strong className="text-slate-700">{fmt(detail.totalAmount)}</strong></span>
        <span>Tax: <strong className="text-amber-700">{fmt(detail.totalTax)}</strong></span>
        <span>Discount: <strong>{fmt(detail.totalDiscount)}</strong></span>
        {detail.additionalDiscount ? <span>Add. Discount: <strong>{fmt(detail.additionalDiscount)} ({detail.additionalDiscountType})</strong></span> : null}
        {detail.roundOff ? <span>Round Off: <strong>{fmt(detail.roundOff)}</strong></span> : null}
        {detail.advanceAmount ? <span>Advance: <strong className="text-emerald-700">{fmt(detail.advanceAmount)}</strong></span> : null}
        <span className="ml-auto font-bold text-slate-800">Grand Total: <strong className="text-base">{fmt(detail.grandTotal)}</strong></span>
      </div>
    </div>
  );
}

function InfoRow({ label, value, bold, highlight }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-slate-400 whitespace-nowrap">{label}:</span>
      <span className={`${bold ? "font-bold text-slate-900" : highlight ? "font-semibold text-indigo-700" : "text-slate-700"} truncate`}>{value}</span>
    </div>
  );
}

function EntryTable({ title, icon, color, entries, showDept, showBatch }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <span className={`text-[11px] font-bold text-${color}-700 uppercase tracking-wider`}>{title} ({entries.length})</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow className={`bg-${color}-50/50`}>
            <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2">Name</TableHead>
            {showDept && <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2">Department</TableHead>}
            {showBatch && <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2">Batch</TableHead>}
            <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2 text-center">Qty</TableHead>
            <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2 text-right">Unit Price</TableHead>
            <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2 text-right">Tax %</TableHead>
            <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2 text-right">Disc %</TableHead>
            <TableHead className="text-[9px] font-bold uppercase tracking-wider text-slate-500 py-1.5 px-2 text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((e, ei) => (
            <TableRow key={ei} className="hover:bg-white/60">
              <TableCell className="text-[11px] font-medium text-slate-800 px-2 py-1.5 max-w-[220px] truncate">{e.name}</TableCell>
              {showDept && <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{e.details?.department || "-"}</TableCell>}
              {showBatch && <TableCell className="text-[11px] font-mono text-slate-500 px-2 py-1.5">{e.details?.batchNumber || "-"}</TableCell>}
              <TableCell className="text-[11px] text-center tabular-nums px-2 py-1.5">{e.quantity}</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(e.unitPrice)}</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums text-amber-600 px-2 py-1.5">{e.tax || 0}%</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{e.discount || 0}%</TableCell>
              <TableCell className="text-[11px] text-right tabular-nums font-semibold px-2 py-1.5">{fmt(e.total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
